/**
 * Where a criterion's values come from.
 *
 * Every criterion on a track needs a value on every item before S5 can rank, and
 * criteria are not alike: a contracted cost is a fact on the item, customer impact
 * is the room's judgment, effort is one person's estimate, RICE's reach is worked
 * out by the method. So each criterion says where its values come from — one of
 * five answers, kept in `cycle_attribute_configs.collection_method` on the track's
 * row (migration 1062; the column dates from 098), defaulted from the workspace's
 * attribute setting and a cycle's override. This file is the one place that names
 * the five, resolves the three levels, and says what each answer means for a cell
 * in the session designer's S4 grid.
 *
 * Owner request 2026-11-03. Canon: glossary › Criterion; Doc 28 §2.2.
 */

import type { CollectionMethod } from '@/stores/prioritization/attributeGovernanceStore';
import { joinNames } from './automationRules';

export type ValuesFrom = CollectionMethod;

export interface ValuesFromAnswer {
  id: ValuesFrom;
  /** Short, the way the column and the S4 line say it: "the record". */
  label: string;
  /** One sentence for the chooser. */
  meaning: string;
  /** How S4 treats a column of this kind. */
  inSession: 'read' | 'one_person' | 'typed' | 'room' | 'method';
}

export const VALUES_FROM: readonly ValuesFromAnswer[] = [
  {
    id: 'any_group_member',
    label: 'the record',
    meaning: "The item's official value, as it stands. Set on the item, before or outside the session, by whoever may propose or accept values.",
    inSession: 'read',
  },
  {
    id: 'specific_person',
    label: 'one person',
    meaning: 'A named person supplies it. Only they may type its column in a session.',
    inSession: 'one_person',
  },
  {
    id: 'facilitator_entry',
    label: 'the facilitator',
    meaning: 'Whoever runs the session enters it, for that session.',
    inSession: 'typed',
  },
  {
    id: 'participant_vote',
    label: 'the room',
    meaning: 'Participants score it in the session; the session combines their scores.',
    inSession: 'room',
  },
  {
    id: 'tool_result',
    label: 'the method',
    meaning: 'The method works it out from other values or votes.',
    inSession: 'method',
  },
];

export const VALUES_FROM_IDS: readonly ValuesFrom[] = VALUES_FROM.map((a) => a.id);

export function isValuesFrom(value: unknown): value is ValuesFrom {
  return typeof value === 'string' && (VALUES_FROM_IDS as readonly string[]).includes(value);
}

export function valuesFromAnswer(id: ValuesFrom | null | undefined): ValuesFromAnswer | null {
  return VALUES_FROM.find((a) => a.id === id) ?? null;
}

/** "the record", "one person (Priya)", or "not said". */
export function valuesFromLabel(
  id: ValuesFrom | null | undefined,
  personName?: string | null,
): string {
  const answer = valuesFromAnswer(id);
  if (!answer) return 'not said';
  if (answer.id === 'specific_person' && personName) return `${personName}`;
  return answer.label;
}

/** The resolved answer for one criterion: the word that counts, and whose it is. */
export interface ResolvedValuesFrom {
  from: ValuesFrom | null;
  personId: string | null;
  /** Which level said it; 'none' when nobody has. */
  saidBy: 'track' | 'cycle' | 'workspace' | 'none';
}

/**
 * Resolve the three levels the way the governance store resolves every other
 * dimension: the track's row, else the cycle's override, else the workspace's
 * attribute template. A level that says nothing (NULL) passes to the next; nobody
 * saying anything is `from: null`, which S4 treats as today's behaviour.
 */
export function resolveValuesFrom(levels: {
  track?: { collectionMethod?: ValuesFrom | string | null; specificPersonId?: string | null } | null;
  cycle?: { collectionMethod?: ValuesFrom | string | null; specificPersonId?: string | null } | null;
  workspace?: { collectionMethod?: ValuesFrom | string | null; specificPersonId?: string | null } | null;
}): ResolvedValuesFrom {
  const order: Array<['track' | 'cycle' | 'workspace', typeof levels.track]> = [
    ['track', levels.track],
    ['cycle', levels.cycle],
    ['workspace', levels.workspace],
  ];
  for (const [saidBy, level] of order) {
    if (level && isValuesFrom(level.collectionMethod)) {
      return {
        from: level.collectionMethod,
        personId: level.collectionMethod === 'specific_person' ? firstPerson(level.specificPersonId) : null,
        saidBy,
      };
    }
  }
  return { from: null, personId: null, saidBy: 'none' };
}

/** specific_person_id has been saved comma-joined by the range modal; the first id is the person. */
function firstPerson(raw: string | null | undefined): string | null {
  const first = (raw ?? '').split(',').map((s) => s.trim()).find(Boolean);
  return first ?? null;
}

/** What one S4 cell does for the person looking at it. */
export type S4CellPolicy =
  /** Typeable: the facilitator's, or not said yet (today's grid). */
  | 'typed'
  /** The item's official value; read, never typed here. */
  | 'record'
  /** One person's; typeable only for them. */
  | 'mine'
  | 'waiting'
  /** The room's or the method's; filled in when the session has run. */
  | 'room'
  | 'method';

export function s4CellPolicy(
  from: ValuesFrom | null | undefined,
  personId: string | null | undefined,
  viewerUserId: string | null | undefined,
): S4CellPolicy {
  switch (from) {
    case 'any_group_member':
      return 'record';
    case 'specific_person':
      return personId && viewerUserId && personId === viewerUserId ? 'mine' : 'waiting';
    case 'participant_vote':
      return 'room';
    case 'tool_result':
      return 'method';
    case 'facilitator_entry':
    default:
      return 'typed';
  }
}

/** Columns the room or the method fill: S4 has to run collaboratively for them. */
export function needsTheRoom(from: ValuesFrom | null | undefined): boolean {
  return from === 'participant_vote' || from === 'tool_result';
}

/**
 * One sentence for the top of S4: "Cost comes from the record · Impact and Effort
 * are scored by the room · Risk is set here". Criteria nobody has spoken for are
 * named last, so the facilitator sees what is still to be said.
 */
export function valuesFromSentence(
  criteria: ReadonlyArray<{ name: string; from: ValuesFrom | null | undefined; personName?: string | null; keptBy?: string | null }>,
): string {
  if (criteria.length === 0) return '';
  const groups = new Map<string, string[]>();
  const push = (key: string, name: string) => groups.set(key, [...(groups.get(key) ?? []), name]);
  for (const c of criteria) {
    switch (c.from) {
      // A criterion a connected system keeps reads "the record, kept by Jira".
      case 'any_group_member': push(c.keptBy ? `record:${c.keptBy}` : 'record', c.name); break;
      case 'specific_person': push(`person:${c.personName ?? 'one person'}`, c.name); break;
      case 'participant_vote': push('room', c.name); break;
      case 'tool_result': push('method', c.name); break;
      case 'facilitator_entry': push('here', c.name); break;
      default: push('unsaid', c.name);
    }
  }
  const phrase = (key: string, names: string[]): string => {
    const list = joinNames(names);
    const many = names.length > 1;
    if (key === 'record') return `${list} ${many ? 'come' : 'comes'} from the record`;
    if (key.startsWith('record:')) return `${list} ${many ? 'come' : 'comes'} from the record, kept by ${key.slice(7)}`;
    if (key === 'room') return `${list} ${many ? 'are' : 'is'} scored by the room`;
    if (key === 'method') return `${list} ${many ? 'are' : 'is'} worked out by the method`;
    if (key === 'here') return `${list} ${many ? 'are' : 'is'} set here`;
    if (key.startsWith('person:')) return `${list} ${many ? 'come' : 'comes'} from ${key.slice(7)}`;
    return `nobody has said where ${list} ${many ? 'come' : 'comes'} from`;
  };
  const order = ['record', 'room', 'method', 'here'];
  const keys = [...groups.keys()].sort((a, b) => {
    const rank = (k: string) => (k === 'unsaid' ? 99 : k.startsWith('person:') ? 50 : k.startsWith('record:') ? 1 : order.indexOf(k) * 2);
    return rank(a) - rank(b);
  });
  return keys.map((k) => phrase(k, groups.get(k)!)).join(' · ');
}

/**
 * What to say when a connected system keeps a criterion's value (canon 01 › Field Source)
 * and the track's answer is not the record. Only an official value ever crosses to Jira or
 * Linear (migration 979), so a session's own value for such a criterion stays here, and
 * the person choosing should know that before the room scores it. Nothing is refused: a
 * session-only value is still a legitimate choice.
 */
export function keeperNote(criterionName: string, keptBy: string | null | undefined, from: ValuesFrom | null | undefined): string | null {
  if (!keptBy) return null;
  if (from === 'any_group_member') return null;
  if (!from) return `${keptBy} keeps ${criterionName}; the record is its natural answer.`;
  return `${keptBy} keeps ${criterionName}. A value scored or typed in a session stays that session's and never reaches ${keptBy}; only the record does.`;
}
